<template>
  <div
    class="flex w-full bg-white h-screen overflow-y-auto items-start make-items-center"
  >
    <!-- Form Column -->
    <div
      class="w-full sm:w-full lg:w-1/2 items-center flex justify-center p-8 md:p-16 h-full select-none"
    >
      <div
        class="max-w-md mx-auto w-full space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700"
      >
        <!-- Header -->
        <div
          class="flex flex-col sm:flex-row items-center justify-center md:justify-between"
        >
          <img :src="logo.url" class="h-9 w-auto" v-if="logo" />
          <span
            class="border-transparent h-4 w-0 border-r sm:h-10 sm:border-brand-ash mx-3"
          ></span>
          <span
            class="text-base md:text-xl font-semibold text-black tracking-tight self-center"
          >
            Set your password
          </span>
        </div>

        <div class="w-full space-y-6">
          <div
            class="bg-gray-50 border border-gray-100 rounded-2xl p-8 text-center space-y-6 shadow-sm relative overflow-hidden"
          >
            <div
              class="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-brand-primary opacity-5 rounded-full blur-2xl"
            ></div>

            <!-- Icon -->
            <div class="flex justify-center">
              <div
                class="w-16 h-16 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center text-brand-primary"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </div>

            <!-- Text Content -->
            <div class="space-y-4">
              <p class="text-brand-primary font-bold text-xl" v-if="user">
                Hello, {{ user.name }}!
              </p>
              <p class="text-brand-secondary text-sm">
                Please create a secure password for your account.
              </p>
            </div>
          </div>

          <!-- Form Area -->
          <form @submit.prevent="handleSubmit" class="space-y-5">
            <!-- Password Field -->
            <div class="space-y-2 text-left">
              <label class="text-sm font-medium text-brand-body ml-1"
                >New Password</label
              >
              <div class="relative">
                <Input
                  v-model="resetpassword.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Enter your password"
                  class="w-full"
                  :inputClass="
                    isSubmit && v$.password.$error ? 'border-red-500' : ''
                  "
                >
                  <template #extra>
                    <span
                      class="absolute right-4 top-1/2 -mt-2.5 cursor-pointer text-theme-dark-shade hover:text-theme-dark"
                      @click="showPassword = !showPassword"
                    >
                      <EyeIcon class="w-5 h-5" v-if="showPassword" />
                      <EyeOffIcon class="w-5 h-5" v-else />
                    </span>
                  </template>
                </Input>
              </div>
              <p
                v-if="isSubmit && v$.password.$error"
                class="text-xs text-red-500 mt-1 ml-1"
              >
                {{ v$.password.$errors[0].$message }}
              </p>
              <p v-else class="text-xs text-brand-secondary mt-1 ml-1">
                Min. 8 characters
              </p>
            </div>

            <!-- Confirm Password Field -->
            <div class="space-y-2 text-left">
              <label class="text-sm font-medium text-brand-body ml-1"
                >Confirm Password</label
              >
              <div class="relative">
                <Input
                  v-model="resetpassword.password_confirmation"
                  :type="lookPassword ? 'text' : 'password'"
                  placeholder="Re-enter your password"
                  class="w-full"
                  :inputClass="
                    isSubmit && v$.password_confirmation.$error
                      ? 'border-red-500'
                      : ''
                  "
                >
                  <template #extra>
                    <span
                      @click="lookPassword = !lookPassword"
                      class="absolute right-4 top-1/2 -mt-2.5 cursor-pointer text-theme-dark-shade hover:text-theme-dark"
                    >
                      <EyeIcon class="w-5 h-5" v-if="lookPassword" />
                      <EyeOffIcon class="w-5 h-5" v-else />
                    </span>
                  </template>
                </Input>
              </div>
              <p
                v-if="isSubmit && v$.password_confirmation.$error"
                class="text-xs text-red-500 mt-1 ml-1"
              >
                {{ v$.password_confirmation.$errors[0].$message }}
              </p>
            </div>

            <!-- Actions -->
            <div class="flex flex-col gap-3 pt-2">
              <Button
                title="Set Password"
                type="submit"
                :loader="resetpassword.loading"
                class="w-full !bg-brand-primary !text-white hover:bg-opacity-90 transition-all font-semibold shadow-md"
              />
            </div>

            <p
              v-if="resetpassword.error.message"
              class="text-red-500 text-sm font-medium text-center bg-red-50 p-3 rounded-lg border border-red-100 flex items-center justify-center gap-2"
            >
              <ExclamationCircleIcon class="h-4 w-4" />
              {{ resetpassword.error.message }}
            </p>
          </form>
        </div>
      </div>
    </div>

    <!-- Info Column -->
    <InfoColumn />
  </div>
</template>

<script setup>
import { ref, onMounted, inject, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import useVuelidate from "@vuelidate/core";
import { required, minLength, sameAs } from "@vuelidate/validators";
import Auth from "@/modules/Auth";
import InfoColumn from "../common/InfoColumn";
import axios1 from "axios";
import {
  EyeIcon,
  EyeOffIcon,
  ExclamationCircleIcon,
} from "@heroicons/vue/outline";

const route = useRoute();
const router = useRouter();
const axios = inject("axios");

const { resetpassword, Passwordreset } = Auth();

const showPassword = ref(false);
const lookPassword = ref(false);
const user = ref(null);
const isSubmit = ref(false);
const logo = ref("");

// Validation rules
const rules = computed(() => ({
  password: {
    required,
    minLength: minLength(8),
  },
  password_confirmation: {
    required,
    sameAsPassword: sameAs(resetpassword.password),
  },
}));

const v$ = useVuelidate(rules, resetpassword);

onMounted(() => {
  // Fetch Logo
  axios1.get(process.env.BASE_URL_API + "/logo/get").then((res) => {
    logo.value = res.data;
  });

  // Fetch User Info
  const email = route.path.split("/")[2];
  if (email) {
    axios.api.get("/account/get-user/" + email).then((res) => {
      if (res?.data?.data) {
        user.value = res.data.data;
      }
    });
  }
});

const handleSubmit = async () => {
  isSubmit.value = true;
  const result = await v$.value.$validate();
  if (result) {
    Passwordreset();
  }
};
</script>

<style scoped>
/* Optional: Add smooth transition for the input highlight */
.border-red-500 {
  border-color: #ef4444 !important;
}
</style>
